import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Demandas Processuais ─────────────────────────────────────────────────────
// Prazos do INPI lançados no cadastro do cliente (aba Processos) viram uma
// fila única aqui: semáforo de urgência, filtros por tipo/status/data e
// desfecho ✓ Cumprida / ✗ Não cumprida com anotação. Responsável fixo: Vagner.

// Espelha a lista TIPOS do backend (demandas.js) — antes só 5 dos 12 tipos
// apareciam na tela, então os que o robô da revista cria (Contrarrazões,
// Pagamento de Concessão, Prorrogação…) não podiam ser lançados à mão.
// O .sort no fim garante ordem alfabética em TODA lista que usa esta constante
// (filtro da tela, modal novo, modal do cadastro do cliente) — tipo novo pode
// ser acrescentado em qualquer posição que ele entra no lugar certo sozinho.
// localeCompare pt-BR pra 'Exigência' e 'Vigilância' ordenarem pelo acento certo.
export const TIPOS_DEMANDA = [
  'Oposição', 'Manifestação', 'Recurso', 'Contrarrazões',
  'Exigência de Mérito', 'Exigência Formal', 'Exigência de Pagamento',
  'Pagamento de Concessão', 'Pagamento de Taxa Final', 'Vigilância PAN',
  'Provas de Uso (caducidade)', 'Prorrogação', 'Entrega de Certidão',
].sort((a, b) => a.localeCompare(b, 'pt-BR'));

// Grupos de tipo: alimentam TANTO os atalhos quanto a visão em painéis.
// Um grupo pode juntar mais de um tipo — é o caso do pagamento, em que
// Concessão e Taxa Final são a mesma coisa na prática do escritório.
export const GRUPOS_TIPO = [
  { key: 'manifestacao', label: 'Manifestação',        icone: '📣', cor: '#8b5cf6', tipos: ['Manifestação'] },
  { key: 'oposicao',     label: 'Oposição',            icone: '⚔️', cor: '#dc2626', tipos: ['Oposição'] },
  { key: 'exigencia',    label: 'Exigência de Mérito', icone: '📋', cor: '#d97706', tipos: ['Exigência de Mérito'] },
  { key: 'recurso',      label: 'Recurso',             icone: '⚖️', cor: '#2563eb', tipos: ['Recurso'] },
  { key: 'pagamento',    label: 'Pagamento da Taxa Final / Concessão', curto: 'Pagamento Taxa Final / Concessão',
    icone: '💰', cor: '#16a34a', tipos: ['Pagamento de Concessão', 'Pagamento de Taxa Final'] },
];

// Dias que cada tipo corre a partir da PUBLICAÇÃO do despacho na RPI.
// Mesmos números que o robô usa em despachosInpi.js. null = não tem contagem
// padrão a partir de uma movimentação (o prazo entra na mão).
export const PRAZO_DIAS = {
  'Oposição': 60,
  'Manifestação': 60,
  'Recurso': 60,
  'Contrarrazões': 60,
  'Exigência de Mérito': 60,
  'Exigência Formal': 5,
  'Exigência de Pagamento': 5,
  'Pagamento de Concessão': 60,
  'Pagamento de Taxa Final': 60,
  'Provas de Uso (caducidade)': 60,
  'Vigilância PAN': 180,
  'Prorrogação': null,
  'Entrega de Certidão': null,
};

function token() { return localStorage.getItem('token'); }
function ehVagner() { return localStorage.getItem('nomeUsuario') === 'Vagner Freitas'; }

async function apiJson(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.erro || 'Erro');
  return data;
}

const BRT_MS = 3 * 3600000;
const hojeBrt = () => new Date(Date.now() - BRT_MS).toISOString().slice(0, 10);
const fmtBR = (iso) => iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : '—';
const diasAte = (iso) => Math.round((new Date(iso + 'T12:00:00Z') - new Date(hojeBrt() + 'T12:00:00Z')) / 86400000);
const somarDiasIso = (iso, n) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '') || !Number.isFinite(n)) return '';
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Semáforo: vencida ⚫ · ≤7 dias 🔴 · ≤15 🟠 · folgado 🟢
export function urgencia(d) {
  if (d.status === 'cumprida') return { rotulo: '✓ Cumprida', cor: '#16a34a', bg: '#f0fdf4' };
  if (d.status === 'nao_cumprida') return { rotulo: '✗ Não cumprida', cor: '#dc2626', bg: '#fef2f2' };
  const dias = diasAte(d.prazo);
  if (dias < 0) return { rotulo: `VENCIDA há ${-dias}d`, cor: '#fff', bg: '#111827', solida: true };
  if (dias === 0) return { rotulo: 'VENCE HOJE', cor: '#fff', bg: '#dc2626', solida: true };
  if (dias <= 7) return { rotulo: `vence em ${dias}d`, cor: '#dc2626', bg: '#fef2f2' };
  if (dias <= 15) return { rotulo: `vence em ${dias}d`, cor: '#b45309', bg: '#fffbeb' };
  return { rotulo: `vence em ${dias}d`, cor: '#15803d', bg: '#f0fdf4' };
}

const inp = { padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', boxSizing: 'border-box', background: '#fff', height: 26 };
const lbl = { fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 };
const modalBase = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };

function copiarTexto(t) {
  const fallback = () => {
    const el = document.createElement('textarea');
    el.value = t; document.body.appendChild(el); el.select();
    document.execCommand('copy'); document.body.removeChild(el);
  };
  if (navigator.clipboard) navigator.clipboard.writeText(t).catch(fallback);
  else fallback();
}

// ── Campo de prazo: digitado à mão ou contado da movimentação ──
// A conta é a mesma do robô da revista: data de publicação + os dias do tipo.
function CampoPrazo({ tipo, modo, setModo, dataMov, setDataMov, prazoManual, setPrazoManual, prazoFinal }) {
  const dias = PRAZO_DIAS[tipo];
  const temContagem = Number.isFinite(dias);

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '8px 10px', background: '#f9fafb', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 7 }}>
        {[{ k: 'auto', label: 'Contar da movimentação' }, { k: 'manual', label: 'Digitar o prazo' }].map(o => (
          <label key={o.k} style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" checked={modo === o.k} onChange={() => setModo(o.k)} /> {o.label}
          </label>
        ))}
      </div>

      {modo === 'auto' ? (
        <>
          <label style={lbl}>Data da movimentação (publicação na RPI) <span style={{ color: '#dc2626' }}>*</span></label>
          <input type="date" value={dataMov} onChange={e => setDataMov(e.target.value)} style={{ ...inp, width: 155 }} />
          <div style={{ fontSize: 12, marginTop: 6 }}>
            {!tipo
              ? <span style={{ color: '#9ca3af' }}>Escolha o tipo para o sistema saber quantos dias contar.</span>
              : !temContagem
                ? <span style={{ color: '#b45309' }}>⚠ “{tipo}” não tem contagem padrão a partir de uma movimentação — use “Digitar o prazo”.</span>
                : !dataMov
                  ? <span style={{ color: '#6b7280' }}>Vai contar <strong>{dias} dias</strong> a partir da publicação.</span>
                  : <span style={{ color: '#111827' }}>{dias} dias → prazo em <strong>{fmtBR(prazoFinal)}</strong></span>}
          </div>
        </>
      ) : (
        <>
          <label style={lbl}>Prazo pra cumprir <span style={{ color: '#dc2626' }}>*</span></label>
          <input type="date" value={prazoManual} onChange={e => setPrazoManual(e.target.value)} style={{ ...inp, width: 155 }} />
        </>
      )}
    </div>
  );
}

// ── Modal Nova demanda (a partir da tela, sem passar pelo cadastro) ──
// Escolhe o cliente pela busca, sugere os processos que já estão na aba
// Processos dele, e monta o prazo à mão ou contando da movimentação.
export function ModalNovaDemanda({ onFechar, onSalvo }) {
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [procSel, setProcSel] = useState('');       // índice na lista, ou 'outro'
  const [procManual, setProcManual] = useState('');
  const [marcaManual, setMarcaManual] = useState('');
  const [tipo, setTipo] = useState('');
  const [modo, setModo] = useState('auto');
  const [dataMov, setDataMov] = useState('');
  const [prazoManual, setPrazoManual] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Mesma regra do robô da revista (prazosRpi.js) pra a lista não ficar com
  // dois padrões: quase nenhum cliente tem 'nome' preenchido, então na prática
  // o que aparece é a marca. O 'responsavel' entra só como apoio visual na
  // busca — é o campo que de fato tem o nome da pessoa (417 de 457).
  const nomeCli = (c) => c.nome || c.razao_social || c.nome_marca || '(sem nome)';
  const docCli = (c) => c.cnpj || c.cpf || '';

  // Busca com atraso: não bate na API a cada tecla
  useEffect(() => {
    const t = busca.trim();
    if (cliente || t.length < 2) { setAchados([]); setBuscando(false); return; }
    setBuscando(true);
    const timer = setTimeout(() => {
      apiJson(`/clientes?busca=${encodeURIComponent(t)}`)
        .then(l => setAchados((l || []).slice(0, 8)))
        .catch(() => setAchados([]))
        .finally(() => setBuscando(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [busca, cliente]);

  // Processos do cliente: a lista da aba Processos + o par legado do topo
  const processos = React.useMemo(() => {
    if (!cliente) return [];
    const lista = Array.isArray(cliente.processos) ? cliente.processos.filter(p => p && p.numero_processo) : [];
    if (!lista.length && cliente.numero_processo) {
      return [{ numero_processo: cliente.numero_processo, nome_marca: cliente.nome_marca || '', situacao: '' }];
    }
    return lista;
  }, [cliente]);

  const escolhido = (procSel !== '' && procSel !== 'outro') ? processos[Number(procSel)] : null;
  const processoNumero = procSel === 'outro' ? procManual.trim() : (escolhido?.numero_processo || '');
  const marca = procSel === 'outro' ? marcaManual.trim() : (escolhido?.nome_marca || '');

  const dias = PRAZO_DIAS[tipo];
  const prazoAuto = Number.isFinite(dias) ? somarDiasIso(dataMov, dias) : '';
  const prazoFinal = modo === 'auto' ? prazoAuto : prazoManual;

  function escolherCliente(c) {
    setCliente(c); setAchados([]); setErro('');
    const lista = Array.isArray(c.processos) ? c.processos.filter(p => p && p.numero_processo) : [];
    if (lista.length === 1) setProcSel('0');   // só um processo: já vem marcado
    else setProcSel('');
  }

  async function salvar() {
    if (!cliente) return setErro('Escolha o cliente.');
    if (!processoNumero) return setErro('Escolha o processo ou digite o número.');
    if (!tipo) return setErro('Escolha o tipo da demanda.');
    if (!prazoFinal) return setErro(modo === 'auto' ? 'Informe a data da movimentação.' : 'Informe o prazo pra cumprir.');
    setErro(''); setSalvando(true);
    try {
      const nota = (modo === 'auto' && dataMov)
        ? `${obs ? obs.trim() + ' · ' : ''}Movimentação publicada em ${fmtBR(dataMov)} — prazo de ${dias} dias.`
        : obs.trim();
      await apiJson('/demandas', {
        method: 'POST',
        body: JSON.stringify({
          cliente_id: cliente._id, cliente_nome: nomeCli(cliente),
          processo_numero: processoNumero, marca, tipo, prazo: prazoFinal, observacao: nota,
        }),
      });
      onSalvo();
    } catch (e) { setErro(e.message); setSalvando(false); }
  }

  return (
    <div style={modalBase} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 520, maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>⚖️ Nova demanda processual</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '10px 14px' }}>
          {/* 1. Cliente */}
          <label style={lbl}>1. Cliente <span style={{ color: '#dc2626' }}>*</span></label>
          {cliente ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 3, padding: '6px 8px', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0a1628', flex: 1 }}>
                {nomeCli(cliente)}
                {cliente.responsavel ? <span style={{ fontWeight: 400, color: '#6b7280' }}> · {cliente.responsavel}</span> : null}
                {docCli(cliente) ? <span style={{ fontWeight: 400, color: '#9ca3af' }}> · {docCli(cliente)}</span> : null}
              </span>
              <button onClick={() => { setCliente(null); setBusca(''); setProcSel(''); }}
                style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, fontSize: 11, padding: '2px 8px', cursor: 'pointer', color: '#374151' }}>trocar</button>
            </div>
          ) : (
            <>
              <input value={busca} onChange={e => setBusca(e.target.value)} autoFocus
                placeholder="Nome, CPF/CNPJ, marca ou nº do processo…" style={{ ...inp, width: '100%' }} />
              <div style={{ fontSize: 11, color: '#9ca3af', margin: '3px 0 8px' }}>
                {buscando ? 'buscando…' : busca.trim().length < 2 ? 'digite ao menos 2 letras' : achados.length ? `${achados.length} encontrado(s)` : 'nenhum cliente encontrado'}
              </div>
              {achados.length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 3, marginBottom: 10, maxHeight: 190, overflowY: 'auto' }}>
                  {achados.map(c => {
                    const qtd = Array.isArray(c.processos) ? c.processos.filter(p => p && p.numero_processo).length : (c.numero_processo ? 1 : 0);
                    return (
                      <button key={c._id} type="button" onClick={() => escolherCliente(c)}
                        style={{ width: '100%', textAlign: 'left', background: '#fff', border: 'none', font: 'inherit', padding: '5px 8px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <span style={{ color: '#111827' }}>
                          {nomeCli(c)}
                          {c.responsavel ? <span style={{ color: '#6b7280' }}> · {c.responsavel}</span> : null}
                          {docCli(c) ? <span style={{ color: '#9ca3af' }}> · {docCli(c)}</span> : null}
                        </span>
                        <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{qtd} proc.</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* 2. Processo */}
          {cliente && (
            <>
              <label style={lbl}>2. Processo <span style={{ color: '#dc2626' }}>*</span></label>
              {processos.length === 0 && (
                <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 3, padding: '5px 8px', marginBottom: 6 }}>
                  Este cliente não tem processo na aba Processos — digite o número abaixo.
                </div>
              )}
              <select value={procSel} onChange={e => { setProcSel(e.target.value); setErro(''); }} style={{ ...inp, width: '100%', marginBottom: 6 }}>
                <option value="">Escolha o processo…</option>
                {processos.map((pr, i) => (
                  <option key={`${pr.numero_processo}-${i}`} value={String(i)}>
                    {pr.nome_marca || '(sem marca)'} — {pr.numero_processo}{pr.situacao ? ` · ${pr.situacao}` : ''}
                  </option>
                ))}
                <option value="outro">✍️ Outro — digitar o número na mão</option>
              </select>
              {procSel === 'outro' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                  <div>
                    <label style={lbl}>Nº do processo</label>
                    <input value={procManual} onChange={e => setProcManual(e.target.value)} placeholder="937632937" style={{ ...inp, width: '100%' }} />
                  </div>
                  <div>
                    <label style={lbl}>Marca</label>
                    <input value={marcaManual} onChange={e => setMarcaManual(e.target.value)} placeholder="nome da marca" style={{ ...inp, width: '100%' }} />
                  </div>
                </div>
              )}
              <div style={{ height: 4 }} />

              {/* 3. Tipo */}
              <label style={lbl}>3. Tipo da demanda <span style={{ color: '#dc2626' }}>*</span></label>
              <select value={tipo} onChange={e => { setTipo(e.target.value); setErro(''); }} style={{ ...inp, width: '100%', marginBottom: 8 }}>
                <option value="">Escolha…</option>
                {TIPOS_DEMANDA.map(t => (
                  <option key={t} value={t}>{t}{Number.isFinite(PRAZO_DIAS[t]) ? ` (${PRAZO_DIAS[t]} dias)` : ''}</option>
                ))}
              </select>

              {/* 4. Prazo */}
              <label style={lbl}>4. Prazo <span style={{ color: '#dc2626' }}>*</span></label>
              <CampoPrazo tipo={tipo} modo={modo} setModo={setModo} dataMov={dataMov} setDataMov={setDataMov}
                prazoManual={prazoManual} setPrazoManual={setPrazoManual} prazoFinal={prazoFinal} />

              <label style={lbl}>Observação</label>
              <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
                placeholder="Ex.: indeferimento por colidência com a marca XYZ…"
                style={{ ...inp, width: '100%', height: 'auto', resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }} />
            </>
          )}

          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>
            Responsável: <strong>Vagner Freitas</strong> · aviso no celular 7 dias antes, 1 dia antes e no dia.
          </div>
          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={salvar} disabled={salvando || !cliente}
              style={{ background: '#0a1628', color: '#fff', padding: '5px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600, border: 'none', cursor: salvando || !cliente ? 'default' : 'pointer', opacity: salvando || !cliente ? 0.5 : 1 }}>
              {salvando ? 'Salvando…' : 'Lançar demanda'}
            </button>
            <button onClick={onFechar} style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', borderRadius: 4, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal criar/editar demanda (usado aqui e no cadastro do cliente) ──
export function ModalDemanda({ demanda, clienteInfo, onFechar, onSalvo }) {
  const [tipo, setTipo] = useState(demanda?.tipo || '');
  const [prazo, setPrazo] = useState(demanda?.prazo || '');

  // Prazo automático: quando o modal veio de um marco da RPI, a data de
  // publicação chega em clienteInfo.data_publicacao ('DD/MM/AAAA') — ao
  // escolher o tipo, o prazo preenche sozinho (publicação + dias do tipo),
  // e continua editável na mão
  const dataPub = clienteInfo?.data_publicacao || '';
  function prazoSugerido(t) {
    const dias = PRAZO_DIAS[t];
    const m = String(dataPub).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (!dias || !m) return '';
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    d.setDate(d.getDate() + dias);
    return d.toISOString().slice(0, 10);
  }
  function aoTrocarTipo(t) {
    setTipo(t);
    const sug = prazoSugerido(t);
    if (sug) setPrazo(sug);
  }
  const [obs, setObs] = useState(demanda?.observacao || '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const info = demanda || clienteInfo || {};

  async function salvar() {
    if (!tipo) return setErro('Escolha o tipo da demanda.');
    if (!prazo) return setErro('Informe o prazo para cumprir.');
    setErro('');
    setSalvando(true);
    try {
      if (demanda) {
        await apiJson(`/demandas/${demanda._id}`, { method: 'PUT', body: JSON.stringify({ tipo, prazo, observacao: obs }) });
      } else {
        await apiJson('/demandas', { method: 'POST', body: JSON.stringify({ ...clienteInfo, tipo, prazo, observacao: obs }) });
      }
      onSalvo();
    } catch (e) { setErro(e.message); setSalvando(false); }
  }

  return (
    <div style={modalBase} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 440, maxWidth: '95vw', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>⚖️ {demanda ? 'Editar demanda' : 'Nova demanda processual'}</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: '#374151', background: '#f9fafb', padding: '6px 8px', borderRadius: 3, marginBottom: 10, border: '1px solid #e5e7eb' }}>
            <strong>{info.cliente_nome || '—'}</strong>
            {info.marca ? <> · ™ {info.marca}</> : ''}
            {info.processo_numero ? <> · proc. {info.processo_numero}</> : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lbl}>Tipo da demanda <span style={{ color: '#dc2626' }}>*</span></label>
              <select value={tipo} onChange={e => { aoTrocarTipo(e.target.value); setErro(''); }} style={{ ...inp, width: '100%' }}>
                <option value="">Escolha…</option>
                {TIPOS_DEMANDA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Prazo pra cumprir <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="date" value={prazo} onChange={e => { setPrazo(e.target.value); setErro(''); }} style={{ ...inp, width: '100%' }} />
            </div>
          </div>
          {dataPub && tipo && PRAZO_DIAS[tipo] ? (
            <div style={{ fontSize: 11, color: '#1d4ed8', margin: '-4px 0 8px' }}>
              📅 Prazo sugerido: publicação {dataPub} + {PRAZO_DIAS[tipo]} dias — ajuste se precisar.
            </div>
          ) : null}
          <label style={lbl}>Observação</label>
          <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
            placeholder="Ex.: oposição da marca XYZ, publicada na RPI 2896…"
            style={{ ...inp, width: '100%', height: 'auto', resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }} />
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>
            Responsável: <strong>Vagner Freitas</strong> · aviso no celular 7 dias antes, 1 dia antes e no dia.
          </div>
          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={salvar} disabled={salvando}
              style={{ background: '#0a1628', color: '#fff', padding: '5px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>
              {salvando ? 'Salvando…' : demanda ? 'Salvar' : 'Lançar demanda'}
            </button>
            <button onClick={onFechar} style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', borderRadius: 4, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal desfecho (✓ cumprir / ✗ não cumprida) ──
export function ModalDesfechoDemanda({ demanda, acao, onFechar, onSalvo }) {
  const [resultado, setResultado] = useState('');
  const [salvando, setSalvando] = useState(false);
  const cumprir = acao === 'cumprir';
  async function salvar() {
    setSalvando(true);
    try {
      await apiJson(`/demandas/${demanda._id}/${cumprir ? 'cumprir' : 'nao-cumprida'}`, { method: 'PUT', body: JSON.stringify({ resultado }) });
      onSalvo();
    } catch (e) { window.alert(e.message); setSalvando(false); }
  }
  return (
    <div style={modalBase} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 440, maxWidth: '95vw', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{cumprir ? '✓ Cumprir demanda' : '✗ Não cumprida'}</span>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: '#374151', background: '#f9fafb', padding: '6px 8px', borderRadius: 3, marginBottom: 10, border: '1px solid #e5e7eb' }}>
            <strong>{demanda.tipo}</strong> — {demanda.cliente_nome}{demanda.processo_numero ? ` · proc. ${demanda.processo_numero}` : ''} · prazo {fmtBR(demanda.prazo)}
          </div>
          <label style={lbl}>{cumprir ? 'O que foi feito?' : 'Por que não foi cumprida?'}</label>
          <textarea value={resultado} onChange={e => setResultado(e.target.value)} rows={3} autoFocus
            placeholder={cumprir ? 'Ex.: recurso protocolado no INPI em 14/07…' : 'Ex.: cliente não enviou os documentos…'}
            style={{ ...inp, width: '100%', height: 'auto', resize: 'vertical', fontFamily: 'inherit', marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={salvar} disabled={salvando}
              style={{ background: cumprir ? '#16a34a' : '#dc2626', color: '#fff', padding: '5px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>
              {salvando ? 'Salvando…' : cumprir ? '✓ Confirmar cumprimento' : '✗ Marcar não cumprida'}
            </button>
            <button onClick={onFechar} style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', borderRadius: 4, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Página ──
// ⚖️ Gerador de peças (v2 — 04/09): dossiê da base de revistas → fichas
// oficiais do pePI capturadas em background → redação por SEÇÕES (ordem do
// modelo do escritório) editáveis na tela → prints do advogado com legenda e
// seção destino → Word completo com figuras numeradas, comparativo dos sinais
// e tabela de diluição. O texto corrido continua disponível (copiar/.txt).
const SECOES_DESTINO = [
  ['uso', 'VIII — Uso efetivo / público-alvo'], ['fatos', 'II — Fatos'], ['conjunto', 'VI — Impressão de conjunto'],
  ['especialidade', 'VII — Especialidade'], ['diluicao', 'V — Diluição'], ['precedentes', 'IX — Precedentes'], ['anexos', 'Anexos complementares'],
];
function ModalMinutaRecurso({ demanda, onFechar }) {
  const [dossie, setDossie] = useState(null);
  const [erro, setErro] = useState('');
  const [obs, setObs] = useState('');
  const [gerando, setGerando] = useState(false);
  const [secoes, setSecoes] = useState([]);
  const [nota, setNota] = useState('');
  const [espec, setEspec] = useState(''); // especificação restringida do pedido subsidiário
  const [ultima, setUltima] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [baixandoDocx, setBaixandoDocx] = useState(false);
  const [prints, setPrints] = useState(null);
  const [anexos, setAnexos] = useState([]);
  const [legenda, setLegenda] = useState('');
  const [secaoAnexo, setSecaoAnexo] = useState('uso');
  const [subindo, setSubindo] = useState(false);
  const [opcoes, setOpcoes] = useState({ pepi: true, comparativo: true, diluicao: true });

  const tipo = ['Recurso', 'Exigência de Mérito', 'Manifestação', 'Oposição'].includes(demanda.tipo) ? demanda.tipo : 'Recurso';
  const proc = demanda.processo_numero;

  useEffect(() => {
    apiJson(`/minuta-recurso/dossie/${proc}?tipo=${encodeURIComponent(tipo)}&cliente_id=${encodeURIComponent(demanda.cliente_id || '')}`)
      .then(setDossie).catch(e => setErro(e.message));
    apiJson(`/minuta-recurso/prints/${proc}`).then(r => setPrints(r.job)).catch(() => {});
    apiJson(`/minuta-recurso/anexos/${proc}`).then(r => setAnexos(r.anexos || [])).catch(() => {});
    apiJson(`/minuta-recurso/secoes/${proc}`).then(r => { if (r.secoes?.length) setUltima(r); }).catch(() => {});
  }, [proc, demanda.cliente_id, tipo]);

  // Enquanto o job das fichas roda, consulta a cada 5s
  useEffect(() => {
    if (prints?.status !== 'rodando') return;
    const t = setInterval(() => apiJson(`/minuta-recurso/prints/${proc}`).then(r => setPrints(r.job)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [prints?.status, proc]);

  async function capturarFichas() {
    try { const r = await apiJson(`/minuta-recurso/prints/${proc}?tipo=${encodeURIComponent(tipo)}`, { method: 'POST' }); setPrints(r.job); }
    catch (e) { setErro(e.message); }
  }

  async function gerar() {
    setGerando(true); setErro('');
    try {
      const r = await apiJson('/minuta-recurso/gerar', { method: 'POST', body: JSON.stringify({ processo: proc, tipo, cliente_id: demanda.cliente_id || '', observacoes: obs }) });
      setSecoes(r.secoes || []); setNota(r.nota_advogado || ''); setEspec(r.especificacao_restringida || '');
    } catch (e) { setErro(e.message); }
    setGerando(false);
  }

  async function subirAnexo(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSubindo(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('arquivo', f); fd.append('legenda', legenda); fd.append('secao', secaoAnexo);
        const res = await fetch(`/minuta-recurso/anexo/${proc}`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.erro || 'Falha no upload');
        setAnexos(d.anexos || []);
      }
      setLegenda('');
    } catch (err) { alert(err.message); }
    setSubindo(false);
    e.target.value = '';
  }

  async function removerAnexo(arquivo) {
    try { const r = await apiJson(`/minuta-recurso/anexo/${proc}/${arquivo}`, { method: 'DELETE' }); setAnexos(r.anexos || []); }
    catch (err) { alert(err.message); }
  }

  const textoCorrido = () => secoes.map(s => `${s.titulo}\n\n${(s.paragrafos || []).join('\n\n')}`).join('\n\n') + (nota ? `\n\n— — —\nNOTA AO ADVOGADO (não integra a peça):\n${nota}` : '');

  function baixarTxt() {
    const blob = new Blob([textoCorrido()], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `minuta_${tipo.toLowerCase().replace(/[^a-z]/g, '_')}_${proc}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  // Word completo: manda as seções COMO ESTÃO NA TELA (com as edições) e o
  // backend embute as figuras (pePI, comparativo, diluição, prints)
  async function baixarWord() {
    setBaixandoDocx(true);
    try {
      const res = await fetch('/minuta-recurso/recurso-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ processo: proc, tipo, cliente_id: demanda.cliente_id || '', secoes, opcoes, especificacao_restringida: espec }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.erro || 'Erro ao gerar o Word'); }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Recurso_${proc}.docx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) { alert(e.message); }
    setBaixandoDocx(false);
  }

  const btn = (extra = {}) => ({ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', ...extra });
  const fichasOk = (prints?.itens || []).filter(i => i.arquivo).length;

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 900, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#4c1d95', color: '#fff', padding: '9px 14px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 2 }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>⚖️ {tipo} — {demanda.marca || demanda.cliente_nome} · proc. {proc}</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 14 }}>
          {erro && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, padding: '7px 10px', fontSize: 12.5, marginBottom: 10 }}>⚠ {erro}</div>}

          {!secoes.length && (dossie ? (
            <>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                <strong>1.</strong> Confira os fatos que o sistema levantou na base de revistas (RPI {dossie.base?.revista || '—'}):
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '9px 12px', fontSize: 12.5, marginBottom: 10 }}>
                <div><strong>{tipo === 'Oposição' ? 'Pedido do TERCEIRO (alvo):' : 'Pedido:'}</strong> {dossie.pedido.marca} · proc. {dossie.pedido.numero} · {dossie.pedido.apresentacao || '—'} · classes {dossie.pedido.classes.map(c => c.codigo).join(', ') || '—'} · {dossie.pedido.situacao}</div>
                {dossie.despacho_gatilho
                  ? <div style={{ marginTop: 3 }}><strong>{dossie.despacho_gatilho.nome}</strong> na RPI {dossie.despacho_gatilho.rpi} ({dossie.despacho_gatilho.data}) · prazo: <strong style={{ color: '#b91c1c' }}>{dossie.prazo || '—'}</strong>{dossie.prazo_detalhe?.prorrogado ? ` (caiu em ${dossie.prazo_detalhe.motivo || 'dia sem expediente'} → prorrogado para o dia útil seguinte)` : ''}</div>
                  : <div style={{ marginTop: 3, color: '#b45309' }}>⚠ Não achei o despacho que abre esta peça na base — o prazo e os fatos dele ficarão de fora.</div>}
                {dossie.recurso_ja_protocolado && <div style={{ marginTop: 3, color: '#b45309' }}>⚠ Já consta recurso protocolado (RPI {dossie.recurso_ja_protocolado.rpi}, {dossie.recurso_ja_protocolado.data}) — esta peça seria aditamento/nova via.</div>}
                {dossie.cliente?.receita && (
                  <div style={{ marginTop: 3 }}><strong>CNAE do cliente (Receita):</strong> {dossie.cliente.receita.cnae_principal.codigo} {dossie.cliente.receita.cnae_principal.descricao} · aberta em {dossie.cliente.receita.abertura} · {dossie.cliente.receita.situacao}</div>
                )}
                {(dossie.marcas_citadas_no_despacho || []).length > 0 && (
                  <>
                    <div style={{ marginTop: 6 }}><strong>Marcas citadas no despacho ({dossie.marcas_citadas_no_despacho.length}):</strong></div>
                    {dossie.marcas_citadas_no_despacho.map(b => (
                      <div key={b.numero} style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #cbd5e1' }}>
                        <div style={{ fontWeight: 600 }}>{b.marca} — proc. {b.numero} · {b.apresentacao || '—'} · {b.situacao} · {b.titular}{b.uf ? ` (${b.uf})` : ''}{b.data_concessao ? ` · concedida ${b.data_concessao}` : ''}</div>
                        {b.receita
                          ? <div style={{ fontSize: 11.5, color: '#0369a1' }}>CNAE (Receita): {b.receita.cnae_principal.codigo} {b.receita.cnae_principal.descricao} · aberta em {b.receita.abertura}</div>
                          : <div style={{ fontSize: 11.5, color: '#6b7280', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                              <span>CNPJ do titular (para o quadro de CNAE):</span>
                              <input placeholder="00.000.000/0000-00" style={{ ...inp, width: 150, height: 22, fontSize: 11.5 }}
                                onKeyDown={async e => {
                                  if (e.key !== 'Enter') return;
                                  const cnpj = e.target.value.replace(/\D/g, '');
                                  if (cnpj.length !== 14) return;
                                  try {
                                    await apiJson(`/minuta-recurso/cnpj-terceiro/${proc}`, { method: 'PUT', body: JSON.stringify({ numero: b.numero, cnpj }) });
                                    const d = await apiJson(`/minuta-recurso/dossie/${proc}?tipo=${encodeURIComponent(tipo)}&cliente_id=${encodeURIComponent(demanda.cliente_id || '')}`);
                                    setDossie(d);
                                  } catch (err) { setErro(err.message); }
                                }} />
                              <span style={{ color: '#9ca3af' }}>Enter para gravar</span>
                            </div>}
                        {b.classes.map((c, j) => (
                          <div key={j} style={{ fontSize: 11.5, color: c.status && /exclu/i.test(c.status) ? '#9ca3af' : '#374151' }}>
                            <strong>Cl. {c.codigo}{c.status ? ` (${c.status})` : ''}:</strong> {c.especificacao.slice(0, 220)}{c.especificacao.length > 220 ? '…' : ''}
                          </div>
                        ))}
                      </div>
                    ))}
                  </>
                )}
                {(dossie.marcas_do_cliente || []).length > 0 && (
                  <>
                    <div style={{ marginTop: 6 }}><strong>Marcas do cliente que fundamentam ({dossie.marcas_do_cliente.length}):</strong></div>
                    {dossie.marcas_do_cliente.map(b => (
                      <div key={b.numero} style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #86efac' }}>
                        <div style={{ fontWeight: 600 }}>{b.marca} — proc. {b.numero} · {b.situacao}</div>
                      </div>
                    ))}
                  </>
                )}
                {dossie.despacho_gatilho?.texto && (
                  <div style={{ marginTop: 6, fontSize: 11.5, color: '#475569', background: '#eef2f7', borderRadius: 4, padding: '5px 8px' }}>
                    <strong>Texto do despacho:</strong> {dossie.despacho_gatilho.texto.slice(0, 380)}{dossie.despacho_gatilho.texto.length > 380 ? '…' : ''}
                  </div>
                )}
                {dossie.diluicao.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <strong>Diluição no segmento (classes {dossie.diluicao[0]?.classes_consideradas?.join(', ') || '—'}):</strong>{' '}
                    {dossie.diluicao.map(d => `${d.termo}: ${d.titulares_no_segmento} titulares · ${(d.marcas_vivas_no_segmento || []).length} marcas vivas listadas`).join(' · ')}
                  </div>
                )}
              </div>

              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}><strong>2.</strong> Figuras da peça (facilitam a leitura do examinador):</div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12.5 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={capturarFichas} disabled={prints?.status === 'rodando'} style={btn({ color: '#1d4ed8', borderColor: '#93c5fd', fontWeight: 700, opacity: prints?.status === 'rodando' ? .6 : 1 })}>
                    {prints?.status === 'rodando' ? '📸 Capturando fichas do INPI…' : fichasOk ? `📸 Recapturar fichas do INPI (${fichasOk} prontas)` : '📸 Capturar fichas oficiais do INPI (pePI)'}
                  </button>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>pedido + cada anterioridade citada · leva ~1 min por processo</span>
                </div>
                {(prints?.itens || []).length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {prints.itens.map(i => (
                      <span key={i.numero} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: i.arquivo ? '#dcfce7' : i.erro ? '#fee2e2' : '#f3f4f6', color: i.arquivo ? '#166534' : i.erro ? '#991b1b' : '#6b7280' }}>
                        {i.arquivo ? <a href={`/minuta-recurso/imagem/${proc}/${i.arquivo}?t=`} onClick={e => { e.preventDefault(); window.open(`/minuta-recurso/imagem/${proc}/${i.arquivo}`); }} style={{ color: 'inherit' }}>✓ {i.numero}</a> : i.erro ? `✗ ${i.numero}` : `⏳ ${i.numero}`}
                      </span>
                    ))}
                  </div>
                )}
                <label style={{ display: 'block', marginTop: 8, fontSize: 12 }}><input type="checkbox" checked={opcoes.pepi} onChange={e => setOpcoes(o => ({ ...o, pepi: e.target.checked }))} /> Fichas oficiais do pePI nos fatos</label>
                <label style={{ display: 'block', fontSize: 12 }}><input type="checkbox" checked={opcoes.comparativo} onChange={e => setOpcoes(o => ({ ...o, comparativo: e.target.checked }))} /> Quadro comparativo dos sinais (gerado pelo sistema)</label>
                <label style={{ display: 'block', fontSize: 12 }}><input type="checkbox" checked={opcoes.diluicao} onChange={e => setOpcoes(o => ({ ...o, diluicao: e.target.checked }))} /> Tabela das marcas vivas com o elemento comum (diluição)</label>
                <div style={{ marginTop: 8, borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
                  <div style={{ fontSize: 12, marginBottom: 4 }}><strong>Seus prints</strong> (Instagram, site, CNPJ, fachada, notas…) — cada um com legenda e a seção onde entra:</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input value={legenda} onChange={e => setLegenda(e.target.value)} placeholder="Legenda (ex.: Perfil @jeovagire — 4,2 mil seguidores, print de 04/09/2026)" style={{ flex: 1, minWidth: 260, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }} />
                    <select value={secaoAnexo} onChange={e => setSecaoAnexo(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}>
                      {SECOES_DESTINO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <label style={btn({ color: '#4c1d95', borderColor: '#c4b5fd', fontWeight: 700 })}>
                      {subindo ? 'Enviando…' : '📎 Escolher imagens'}
                      <input type="file" accept="image/*" multiple onChange={subirAnexo} style={{ display: 'none' }} disabled={subindo} />
                    </label>
                  </div>
                  {anexos.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      {anexos.map(a => (
                        <div key={a.arquivo} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f1f5f9' }}>
                          <img src={`/minuta-recurso/imagem/${proc}/${a.arquivo}`} alt="" style={{ width: 44, height: 32, objectFit: 'cover', borderRadius: 3, border: '1px solid #e2e8f0' }} onError={e => { e.target.style.display = 'none'; }} />
                          <span style={{ flex: 1 }}>{a.legenda || <em style={{ color: '#9ca3af' }}>sem legenda</em>} <span style={{ color: '#9ca3af' }}>· {(SECOES_DESTINO.find(s => s[0] === a.secao) || [])[1] || a.secao}</span></span>
                          <button onClick={() => removerAnexo(a.arquivo)} style={btn({ padding: '2px 8px', color: '#b91c1c', borderColor: '#fecaca' })}>🗑</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}><strong>3.</strong> Observações pra esta peça <span style={{ color: '#9ca3af' }}>(opcional — provas de uso, público-alvo, o que você já sabe do caso)</span></div>
              <textarea value={obs} onChange={e => setObs(e.target.value)} rows={3}
                placeholder="Ex.: educandário em atividade desde 2002 (CNPJ 04.866.739/0001-31), Instagram com 4 mil seguidores; a titular da anterioridade não responde ao contato; aceitamos restringir a especificação."
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <button onClick={gerar} disabled={gerando}
                  style={{ background: '#4c1d95', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: gerando ? .6 : 1 }}>
                  {gerando ? '✍️ Redigindo a peça por seções… (1-3 min)' : `⚖️ Redigir ${tipo.toLowerCase()}`}
                </button>
                {ultima && <button onClick={() => { setSecoes(ultima.secoes); setNota(ultima.nota_advogado || ''); setEspec(ultima.especificacao_restringida || ''); }} style={btn()}>↺ Reabrir última minuta ({new Date(ultima.gerado_em).toLocaleString('pt-BR')})</button>}
              </div>
            </>
          ) : !erro && <div style={{ color: '#9ca3af', fontSize: 13 }}>Levantando o dossiê do processo…</div>)}

          {secoes.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', position: 'sticky', top: 40, background: '#fff', padding: '6px 0', zIndex: 1 }}>
                <button onClick={baixarWord} disabled={baixandoDocx}
                  style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: baixandoDocx ? .6 : 1 }}>
                  {baixandoDocx ? 'Montando o Word com as figuras…' : '📄 Recurso completo (.docx com figuras)'}
                </button>
                <button onClick={() => { navigator.clipboard?.writeText(textoCorrido()); setCopiado(true); setTimeout(() => setCopiado(false), 1600); }}
                  style={{ background: copiado ? '#16a34a' : '#0a1628', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {copiado ? '✓ Copiado' : '📋 Copiar texto'}
                </button>
                <button onClick={baixarTxt} style={btn()}>⬇️ .txt</button>
                <button onClick={() => setSecoes([])} style={btn()}>↩ Voltar ao dossiê / figuras</button>
                <span style={{ fontSize: 11, color: '#6b7280', alignSelf: 'center' }}>{fichasOk} ficha(s) do pePI · {anexos.length} print(s) seu(s) · salvo em 📝 Anotações</span>
              </div>
              {nota && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '7px 10px', fontSize: 12, color: '#78350f', marginBottom: 10, whiteSpace: 'pre-wrap' }}>
                  <strong>Nota ao advogado (não vai na peça):</strong> {nota}
                </div>
              )}
              {secoes.map((s, i) => (
                <div key={s.id || i} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 12.5, color: '#0a1628', marginBottom: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{s.titulo}</span>
                    <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>
                      {s.id === 'fatos' && opcoes.pepi && fichasOk ? `+ ${fichasOk} ficha(s) do pePI` : ''}
                      {s.id === 'conjunto' && opcoes.comparativo ? '+ comparativo' : ''}
                      {s.id === 'diluicao' && opcoes.diluicao ? '+ tabela de diluição' : ''}
                      {anexos.filter(a => a.secao === s.id).length ? ` + ${anexos.filter(a => a.secao === s.id).length} print(s)` : ''}
                    </span>
                  </div>
                  <textarea value={(s.paragrafos || []).join('\n\n')}
                    onChange={e => setSecoes(prev => prev.map((x, j) => j === i ? { ...x, paragrafos: e.target.value.split(/\n{2,}/) } : x))}
                    style={{ width: '100%', boxSizing: 'border-box', minHeight: 90, height: Math.min(420, 40 + (s.paragrafos || []).join('\n\n').length / 6), padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12.5, lineHeight: 1.6, fontFamily: 'Georgia, serif', resize: 'vertical' }} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Duplicatas da importação ──────────────────────────────────────────────
// Depois de importar, se a demanda lançada repete uma que o robô da revista
// já tinha criado pro mesmo processo e tipo, mostra a situação e oferece
// apagar a automática — pra não ficar o mesmo prazo em duplicidade na fila.
function ModalDuplicatas({ duplicatas, onFechar }) {
  const [marcadas, setMarcadas] = useState(() => new Set(duplicatas.map(d => d.automatica._id)));
  const [apagando, setApagando] = useState(false);
  const [erro, setErro] = useState('');

  function alternar(id) {
    setMarcadas(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function apagar() {
    setErro(''); setApagando(true);
    try {
      for (const id of marcadas) {
        await apiJson(`/demandas/${id}`, { method: 'DELETE' });
      }
      onFechar(true);
    } catch (e) { setErro(e.message); setApagando(false); }
  }

  return (
    <div style={modalBase} onClick={() => onFechar(false)}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 620, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#b45309', padding: '8px 14px', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>⚠ Demandas em duplicidade</span>
          <button onClick={() => onFechar(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <p style={{ fontSize: 12.5, color: '#374151', margin: '0 0 10px' }}>
            {duplicatas.length === 1 ? 'A demanda que você importou já existia' : `${duplicatas.length} das demandas que você importou já existiam`} — o robô da revista
            já tinha criado pro mesmo processo e tipo. Marque as <strong>automáticas</strong> que quer apagar
            pra não ficar o mesmo prazo duas vezes na fila. A que você acabou de importar fica.
          </p>

          {duplicatas.map(({ importada, automatica }) => (
            <div key={automatica._id} style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '8px 10px', marginBottom: 7, background: marcadas.has(automatica._id) ? '#fef2f2' : '#f9fafb' }}>
              {/* width/flex explícitos: o index.css tem 'input { width:100% }'
                  global, que esticava o checkbox e espremia o texto */}
              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={marcadas.has(automatica._id)} onChange={() => alternar(automatica._id)}
                  style={{ width: 16, height: 16, flex: '0 0 16px', marginTop: 2 }} />
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0a1628' }}>
                    {importada.tipo} · proc. {importada.numero}
                    {importada.marca ? <span style={{ fontWeight: 400, color: '#6b7280' }}> · ™ {importada.marca}</span> : null}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 3 }}>
                    🗑 apagar a <strong>automática</strong> 🤖 {automatica.prazo ? `· prazo ${fmtBR(automatica.prazo)}` : ''}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#15803d' }}>
                    ✓ mantém a que você importou ✍️ {importada.prazo ? `· prazo ${fmtBR(importada.prazo)}` : ''}
                  </div>
                </div>
              </label>
            </div>
          ))}

          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={apagar} disabled={apagando || !marcadas.size}
              style={{ background: '#b91c1c', color: '#fff', padding: '6px 16px', borderRadius: 4, fontSize: 13, fontWeight: 700, border: 'none', cursor: marcadas.size ? 'pointer' : 'default', opacity: apagando || !marcadas.size ? 0.5 : 1 }}>
              {apagando ? 'Apagando…' : `Apagar ${marcadas.size} automática(s)`}
            </button>
            <button onClick={() => onFechar(false)}
              style={{ background: '#f3f4f6', color: '#374151', padding: '6px 14px', borderRadius: 4, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>
              Manter as duas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Importar movimentações do pePI ────────────────────────────────────────
// Sobe o PDF da página "Meus Pedidos" do INPI, o navegador lê os processos,
// o servidor diz de quem é cada um, e aqui você só confirma tipo e prazo.
function ModalImportarPepi({ onFechar, onSalvo }) {
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState('');
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [linhas, setLinhas] = useState(null);   // null = ainda não subiu nada
  const [dataMov, setDataMov] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [resumo, setResumo] = useState(null);

  async function aoEscolherArquivo(e) {
    const arq = e.target.files?.[0];
    if (!arq) return;
    setErro(''); setLendo(true); setNomeArquivo(arq.name); setLinhas(null);
    try {
      const { lerPdf } = await import('./pepiPdf');
      const achados = await lerPdf(arq);
      if (!achados.length) {
        setErro('Não achei nenhum processo nesse PDF. Confira se é a página "Meus Pedidos" do pePI salva em PDF.');
        setLendo(false);
        return;
      }
      const r = await apiJson('/demandas/rastrear', {
        method: 'POST',
        body: JSON.stringify({ processos: achados.map(a => ({ numero: a.numero, marca: a.marca, situacao: a.situacao, classe: a.classe })) }),
      });
      const mapaSug = new Map(achados.map(a => [a.numero, a.tipoSugerido]));
      setLinhas(r.resultado.map(x => ({
        ...x,
        tipo: mapaSug.get(x.numero) || '',
        prazo: '',
        prazoManual: false,
        // já vem marcado só o que tem cliente e alguma coisa a fazer
        selecionado: !!(x.cadastrado && mapaSug.get(x.numero)),
      })));
      setResumo({ total: r.total, cadastrados: r.cadastrados });
    } catch (ex) {
      setErro('Falha ao ler o PDF: ' + ex.message);
    }
    setLendo(false);
  }

  // Prazo derivado: data da movimentação + dias do tipo, salvo se editado à mão
  function prazoDe(l) {
    if (l.prazoManual) return l.prazo;
    const dias = PRAZO_DIAS[l.tipo];
    return Number.isFinite(dias) ? somarDiasIso(dataMov, dias) : '';
  }

  function alterar(numero, campo, valor) {
    setLinhas(ls => ls.map(l => {
      if (l.numero !== numero) return l;
      if (campo === 'prazo') return { ...l, prazo: valor, prazoManual: true };
      return { ...l, [campo]: valor };
    }));
  }

  const marcadas = (linhas || []).filter(l => l.selecionado);
  const prontas = marcadas.filter(l => l.tipo && prazoDe(l) && l.cliente_id);
  const faltando = marcadas.length - prontas.length;

  async function lancar() {
    if (!prontas.length) return setErro('Nenhuma linha pronta: marque as que quer lançar e preencha tipo e prazo.');
    setErro(''); setSalvando(true);
    try {
      const r = await apiJson('/demandas/lote', {
        method: 'POST',
        body: JSON.stringify({
          demandas: prontas.map(l => ({
            cliente_id: l.cliente_id, cliente_nome: l.cliente_nome,
            processo_numero: l.numero, marca: l.marca, tipo: l.tipo, prazo: prazoDe(l),
            observacao: `${l.situacao || 'Importado do pePI'}${dataMov ? ` · movimentação de ${fmtBR(dataMov)}` : ''}`,
          })),
        }),
      });
      if (r.recusadas?.length) {
        setErro(`${r.criadas} lançada(s). ${r.recusadas.length} recusada(s): ` + r.recusadas.map(x => `${x.processo} (${x.erro})`).join('; '));
        setSalvando(false);
        return;
      }
      // Alguma das que lancei repete uma automática da revista? Se sim, o
      // fluxo passa pro modal de duplicidade antes de fechar.
      const dups = [];
      for (const l of prontas) {
        const auto = (l.demandas_abertas || []).find(d => d.tipo === l.tipo && d.automatica);
        if (auto) dups.push({ importada: { numero: l.numero, marca: l.marca, tipo: l.tipo, prazo: prazoDe(l) }, automatica: auto });
      }
      onSalvo(dups);
    } catch (ex) { setErro(ex.message); setSalvando(false); }
  }

  const th = { fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.03em', padding: '4px 6px', textAlign: 'left' };
  const td = { fontSize: 11.5, padding: '4px 6px', borderBottom: '1px solid #eef2f6', verticalAlign: 'top' };

  return (
    <div style={modalBase} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 1000, maxWidth: '97vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📄 Importar movimentações do INPI</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '10px 14px', overflowY: 'auto' }}>
          {!linhas && (
            <>
              <p style={{ fontSize: 12.5, color: '#374151', margin: '0 0 8px' }}>
                No pePI, abra <strong>Meus Pedidos</strong>, salve a página como PDF e suba aqui. O sistema acha os processos,
                descobre de quem é cada um e sugere a demanda pela situação — você só confere o prazo.
              </p>
              <input type="file" accept="application/pdf,.pdf" onChange={aoEscolherArquivo} disabled={lendo}
                style={{ ...inp, width: '100%', height: 'auto', padding: 8 }} />
              {lendo && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Lendo {nomeArquivo}…</div>}
            </>
          )}

          {linhas && (
            <>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: '7px 10px', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#374151' }}>
                  <strong>{resumo.total}</strong> processo(s) no PDF · <strong>{resumo.cadastrados}</strong> com cliente cadastrado
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>Movimentação publicada em</span>
                  <input type="date" value={dataMov} onChange={e => setDataMov(e.target.value)} style={{ ...inp, width: 145 }} />
                </div>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>preenche os prazos de uma vez</span>
              </div>

              <div style={{ border: '1px solid #c8d0da', borderRadius: 4, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: '#0a1628' }}>
                      <th style={{ ...th, width: 28 }}></th>
                      <th style={{ ...th, width: 92 }}>Processo</th>
                      <th style={th}>Marca / Cliente</th>
                      <th style={{ ...th, width: 200 }}>Situação no INPI</th>
                      <th style={{ ...th, width: 175 }}>Demanda</th>
                      <th style={{ ...th, width: 130 }}>Prazo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map(l => {
                      const prazo = prazoDe(l);
                      const jaTem = l.demandas_abertas?.some(d => d.tipo === l.tipo);
                      return (
                        <tr key={l.numero} style={{ background: l.selecionado ? '#f0f9ff' : '#fff' }}>
                          <td style={td}>
                            <input type="checkbox" checked={l.selecionado} disabled={!l.cadastrado}
                              onChange={e => alterar(l.numero, 'selecionado', e.target.checked)} />
                          </td>
                          <td style={{ ...td, fontWeight: 700, color: '#0a1628' }}>{l.numero}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600, color: '#0a1628' }}>{l.marca || '—'}</div>
                            {l.cadastrado
                              ? <div style={{ color: '#6b7280' }}>{l.cliente_nome || '(cliente sem nome)'}</div>
                              : <div style={{ color: '#b45309', fontWeight: 600 }}>⚠ processo não cadastrado</div>}
                            {jaTem && <div style={{ color: '#b45309' }}>⚠ já existe {l.tipo} em aberto</div>}
                          </td>
                          <td style={{ ...td, color: '#6b7280' }}>{l.situacao || '—'}</td>
                          <td style={td}>
                            <select value={l.tipo} onChange={e => alterar(l.numero, 'tipo', e.target.value)}
                              style={{ ...inp, width: '100%' }} disabled={!l.cadastrado}>
                              <option value="">— nada a fazer —</option>
                              {TIPOS_DEMANDA.map(t => (
                                <option key={t} value={t}>{t}{Number.isFinite(PRAZO_DIAS[t]) ? ` (${PRAZO_DIAS[t]}d)` : ''}</option>
                              ))}
                            </select>
                          </td>
                          <td style={td}>
                            <input type="date" value={prazo} onChange={e => alterar(l.numero, 'prazo', e.target.value)}
                              style={{ ...inp, width: '100%' }} disabled={!l.cadastrado || !l.tipo} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ fontSize: 12, color: '#374151', marginTop: 8 }}>
                <strong>{prontas.length}</strong> pronta(s) para lançar
                {faltando > 0 && <span style={{ color: '#b45309' }}> · {faltando} marcada(s) sem tipo ou prazo</span>}
              </div>
            </>
          )}

          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>⚠ {erro}</div>}
        </div>

        <div style={{ padding: '8px 14px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
          <button onClick={lancar} disabled={salvando || !prontas.length}
            style={{ background: '#0a1628', color: '#fff', padding: '5px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600, border: 'none', cursor: prontas.length ? 'pointer' : 'default', opacity: salvando || !prontas.length ? 0.5 : 1 }}>
            {salvando ? 'Lançando…' : `Lançar ${prontas.length || ''} demanda(s)`}
          </button>
          <button onClick={onFechar} style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', borderRadius: 4, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Gerar GRU: entrega tudo mastigado pra você concluir no portal ──────────
// Puxa os dados da demanda (CNPJ, código do serviço, processo) e mostra com
// botão de copiar em cada um, na ordem que o portal do INPI pede. Você abre o
// portal já sabendo exatamente o que colar — sem decorar código nem caçar dado.
function ModalGerarGru({ demanda, onFechar, onIrParaTaxa }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState('');

  useEffect(() => {
    apiJson(`/demandas/${demanda._id}/gru-dados`).then(setDados).catch(e => setErro(e.message));
  }, [demanda._id]);

  function copiar(chave, valor) {
    copiarTexto(valor);
    setCopiado(chave);
    setTimeout(() => setCopiado(''), 1500);
  }

  const Campo = ({ ch, rotulo, valor, aviso }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 4, marginBottom: 6, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10.5, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{rotulo}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: valor ? '#0a1628' : '#b45309', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {valor || aviso || '—'}
        </div>
      </div>
      {valor && (
        <button onClick={() => copiar(ch, valor)}
          style={{ background: copiado === ch ? '#dcfce7' : '#0a1628', color: copiado === ch ? '#15803d' : '#fff', border: 'none', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {copiado === ch ? '✓ copiado' : 'copiar'}
        </button>
      )}
    </div>
  );

  return (
    <div style={modalBase} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 480, maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>🧾 Gerar GRU no INPI</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '10px 14px' }}>
          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {erro}</div>}
          {!dados && !erro && <div style={{ color: '#6b7280', fontSize: 13 }}>Montando os dados…</div>}
          {dados && (
            <>
              <p style={{ fontSize: 12, color: '#374151', margin: '0 0 10px' }}>
                Abra o portal e cole cada campo na ordem. Você confere o valor e clica em gerar — o clique final é seu.
              </p>
              <Campo ch="doc" rotulo={`1. ${dados.documento_tipo || 'Documento'} do cliente (busca de cliente)`} valor={dados.documento_fmt} aviso="Cliente sem CNPJ/CPF no cadastro — preencha antes" />
              <div style={{ fontSize: 11.5, color: '#6b7280', margin: '2px 0 8px 2px' }}>Cliente: <strong>{dados.cliente_nome}</strong>{dados.marca ? ` · ™ ${dados.marca}` : ''}</div>
              <Campo ch="serv" rotulo="2. Tipo de serviço" valor="Marcas" />
              <Campo ch="cod" rotulo={`3. Serviço — ${dados.servico_descricao || ''}`} valor={dados.servico_codigo} aviso="Sem código padrão — selecione no portal pela descrição" />
              <Campo ch="proc" rotulo="4. Nº do processo" valor={dados.processo} aviso="Processo não informado" />

              <div style={{ fontSize: 11.5, color: '#6b7280', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '6px 8px', margin: '4px 0 10px' }}>
                💡 No recurso, confira o campo <strong>“Total de classes”</strong> (geralmente 1). O <strong>valor</strong> aparece ao adicionar o serviço.
              </div>

              <a href={dados.portal_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', textAlign: 'center', background: '#0a1628', color: '#fff', padding: '9px', borderRadius: 5, fontSize: 13.5, fontWeight: 700, textDecoration: 'none', marginBottom: 8 }}>
                🔗 Abrir portal do INPI (gerar GRU)
              </a>
              <button onClick={onIrParaTaxa}
                style={{ width: '100%', background: '#fff', color: '#0a1628', padding: '7px', borderRadius: 5, fontSize: 12.5, fontWeight: 600, border: '1px solid #c8d0da', cursor: 'pointer' }}>
                Já gerei — registrar a taxa (valor / nº da GRU)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── GRU: toda petição no INPI tem taxa ────────────────────────────────────
// Três estados: nada gerado · GRU emitida esperando pagamento · paga.
export function estadoTaxa(d) {
  if (d.taxa_paga) return { k: 'paga', rotulo: '💰 Taxa paga', cor: '#15803d', bg: '#f0fdf4', borda: '#bbf7d0' };
  if (d.taxa_gerada) return { k: 'gerada', rotulo: '💰 GRU a pagar', cor: '#b45309', bg: '#fffbeb', borda: '#fde68a' };
  return { k: 'sem', rotulo: '💰 Sem GRU', cor: '#6b7280', bg: '#f3f4f6', borda: '#e5e7eb' };
}

function ModalTaxa({ demanda, onFechar, onSalvo }) {
  const [gerada, setGerada] = useState(!!demanda.taxa_gerada);
  const [geradaEm, setGeradaEm] = useState(demanda.taxa_gerada_em || '');
  const [paga, setPaga] = useState(!!demanda.taxa_paga);
  const [pagaEm, setPagaEm] = useState(demanda.taxa_paga_em || '');
  const [valor, setValor] = useState(demanda.taxa_valor ?? '');
  const [gru, setGru] = useState(demanda.taxa_gru || '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Os dois estados andam juntos: não se paga GRU que não foi emitida, e
  // desmarcar a emissão derruba o pagamento junto.
  function marcarGerada(v) {
    setGerada(v);
    if (v && !geradaEm) setGeradaEm(hojeBrt());
    if (!v) { setPaga(false); setPagaEm(''); }
  }
  function marcarPaga(v) {
    setPaga(v);
    if (v) {
      setGerada(true);
      if (!geradaEm) setGeradaEm(hojeBrt());
      if (!pagaEm) setPagaEm(hojeBrt());
    } else setPagaEm('');
  }

  async function salvar() {
    setErro(''); setSalvando(true);
    try {
      await apiJson(`/demandas/${demanda._id}/taxa`, {
        method: 'PUT',
        body: JSON.stringify({ gerada, gerada_em: geradaEm, paga, paga_em: pagaEm, valor, gru }),
      });
      onSalvo();
    } catch (e) { setErro(e.message); setSalvando(false); }
  }

  const linhaCheck = (marcado, aoMudar, texto, data, aoMudarData, corMarcado) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 9px', borderRadius: 4, marginBottom: 6,
      background: marcado ? corMarcado.bg : '#f9fafb', border: '1px solid ' + (marcado ? corMarcado.borda : '#e5e7eb') }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: marcado ? corMarcado.cor : '#374151', cursor: 'pointer', flex: 1 }}>
        <input type="checkbox" checked={marcado} onChange={e => aoMudar(e.target.checked)} /> {texto}
      </label>
      <input type="date" value={data} disabled={!marcado} onChange={e => aoMudarData(e.target.value)}
        style={{ ...inp, width: 140, opacity: marcado ? 1 : 0.45 }} />
    </div>
  );

  return (
    <div style={modalBase} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, width: 440, maxWidth: '95vw', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#0a1628', padding: '8px 14px', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>💰 Taxa da demanda</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: '#374151', background: '#f9fafb', padding: '6px 8px', borderRadius: 3, marginBottom: 10, border: '1px solid #e5e7eb' }}>
            <strong>{demanda.tipo}</strong> · {demanda.cliente_nome}
            {demanda.processo_numero ? <> · proc. {demanda.processo_numero}</> : null}
          </div>

          {linhaCheck(gerada, marcarGerada, 'GRU gerada', geradaEm, setGeradaEm,
            { bg: '#fffbeb', borda: '#fde68a', cor: '#b45309' })}
          {linhaCheck(paga, marcarPaga, 'Taxa paga', pagaEm, setPagaEm,
            { bg: '#f0fdf4', borda: '#bbf7d0', cor: '#15803d' })}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lbl}>Valor (R$)</label>
              <input type="number" min="0" step="0.01" value={valor} onChange={e => setValor(e.target.value)}
                placeholder="0,00" style={{ ...inp, width: '100%' }} />
            </div>
            <div>
              <label style={lbl}>Nº da GRU / código</label>
              <input value={gru} onChange={e => setGru(e.target.value)} placeholder="ex.: 3022"
                style={{ ...inp, width: '100%' }} />
            </div>
          </div>

          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={salvar} disabled={salvando}
              style={{ background: '#0a1628', color: '#fff', padding: '5px 16px', borderRadius: 4, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            <button onClick={onFechar} style={{ background: '#f3f4f6', color: '#374151', padding: '5px 14px', borderRadius: 4, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 📋 Visão SIMPLES: relatório de conferência por tipo ─────────────────────
// Réplica da listagem que o Vagner montava à mão no Word: MARCA - PROCESSO -
// OK/FALTA PAGAR. Pagou a taxa = OK; tipo de pagamento sem taxa paga = FALTA
// PAGAR; nos demais tipos vale o status da demanda.
const TIPOS_PAGAMENTO_SIMPLES = ['Pagamento de Concessão', 'Pagamento de Taxa Final', 'Exigência de Pagamento'];
function statusSimples(d) {
  if (d.taxa_paga) return ['OK', '#15803d'];
  if (TIPOS_PAGAMENTO_SIMPLES.includes(d.tipo) || d.taxa_gerada) return ['FALTA PAGAR', '#dc2626'];
  if (d.status === 'cumprida') return ['OK', '#15803d'];
  if (d.status === 'nao_cumprida') return ['NÃO CUMPRIDA', '#6b7280'];
  return ['PENDENTE', '#b45309'];
}

function VisaoSimples({ demandas, onAbrir, onRecarregar, onTaxa, onNova }) {
  const [verArquivadas, setVerArquivadas] = useState(false);

  async function arquivar(d, sim) {
    try {
      await apiJson(`/demandas/${d._id}/arquivar-simples`, { method: 'PUT', body: JSON.stringify({ arquivar: sim }) });
      onRecarregar();
    } catch (e) { window.alert(e.message); }
  }

  // Clique no STATUS alterna na hora: PENDENTE → OK (cumpre) e OK → PENDENTE
  // (reabre). FALTA PAGAR abre o registro da taxa — pagar não é um clique só.
  async function alternarStatus(d, rotulo) {
    try {
      if (rotulo === 'FALTA PAGAR') {
        // Controle fino da GRU fica em outro lugar — aqui é só conferência:
        // um clique marca a taxa como PAGA (vira OK), preservando valor/nº
        await apiJson(`/demandas/${d._id}/taxa`, { method: 'PUT', body: JSON.stringify({
          gerada: true, paga: true,
          valor: d.taxa_valor ?? '', gru: d.taxa_gru || '', gerada_em: d.taxa_gerada_em || '',
        }) });
        return onRecarregar();
      }
      if (rotulo === 'PENDENTE') {
        await apiJson(`/demandas/${d._id}/cumprir`, { method: 'PUT', body: JSON.stringify({ resultado: 'Marcada como OK na visão simples' }) });
      } else if (d.taxa_paga) {
        // OK que vem da TAXA PAGA: desfazer = desmarcar o pagamento (a GRU
        // continua gerada, com valor e número preservados) → vira FALTA PAGAR
        await apiJson(`/demandas/${d._id}/taxa`, { method: 'PUT', body: JSON.stringify({
          gerada: true, paga: false,
          valor: d.taxa_valor ?? '', gru: d.taxa_gru || '', gerada_em: d.taxa_gerada_em || '',
        }) });
        if (d.status === 'cumprida') await apiJson(`/demandas/${d._id}/reabrir`, { method: 'PUT' });
      } else {
        await apiJson(`/demandas/${d._id}/reabrir`, { method: 'PUT' });
      }
      onRecarregar();
    } catch (e) { window.alert(e.message); }
  }

  const ativas = demandas.filter(d => !d.oculta_simples);
  const arquivadas = demandas.filter(d => d.oculta_simples);
  const base = (verArquivadas && arquivadas.length) ? arquivadas : ativas;

  // Pendências em cima, OK embaixo; dentro de cada bloco, prazo mais perto primeiro
  const peso = (d) => statusSimples(d)[0] === 'OK' ? 1 : 0;
  const ordenar = (ds) => [...ds].sort((a, b) => peso(a) - peso(b) || String(a.prazo || '9999').localeCompare(String(b.prazo || '9999')));

  const grupos = [];
  for (const t of TIPOS_DEMANDA) {
    const ds = base.filter(d => d.tipo === t);
    if (ds.length) grupos.push([t, ordenar(ds)]);
  }
  const fora = base.filter(d => !TIPOS_DEMANDA.includes(d.tipo));
  if (fora.length) grupos.push(['Outras', ordenar(fora)]);

  const fmtPrazo = (iso) => iso ? iso.slice(0, 10).split('-').reverse().join('/') : '';
  // Pagamento de Taxa Final: o prazo é controlado em outro lugar — aqui a
  // data é desconsiderada (não aparece nem grita em vermelho)
  const semPrazo = (d) => d.tipo === 'Pagamento de Taxa Final';
  const linhaTexto = (d) => `${(d.marca || d.cliente_nome || '?').toUpperCase()} - ${d.processo_numero || 's/nº'}${d.prazo && !semPrazo(d) ? ' - ' + fmtPrazo(d.prazo) : ''} - ${statusSimples(d)[0]}`;
  const copiarTudo = () => {
    const texto = grupos.map(([t, ds]) =>
      t.toUpperCase() + '\n' + ds.map((d, i) => `${i + 1}. ${linhaTexto(d)}`).join('\n')
    ).join('\n\n');
    copiarTexto(texto);
    window.alert('Lista copiada! É só colar no Word ou no WhatsApp.');
  };

  if (!grupos.length && !arquivadas.length) {
    return <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Nenhuma demanda neste filtro.</div>;
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '4px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          📋 Listagem de conferência ({ativas.length}){verArquivadas && arquivadas.length ? ' — vendo ARQUIVADAS' : ''}
        </span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button onClick={onNova} title="Lançar nova demanda: processo, tipo e prazo (manual ou calculado da publicação)"
            style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
            ＋ NOVO
          </button>
          {arquivadas.length > 0 && (
            <button onClick={() => setVerArquivadas(v => !v)} title="Demandas arquivadas saem da listagem sem mexer no status"
              style={{ background: verArquivadas ? '#b45309' : '#fff', color: verArquivadas ? '#fff' : '#374151', border: '1px solid #c8d0da', borderRadius: 4, padding: '3px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
              🗄️ Arquivadas ({arquivadas.length})
            </button>
          )}
          <button onClick={copiarTudo} title="Copia a lista em texto puro, agrupada por tipo"
            style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
            📋 Copiar lista
          </button>
        </span>
      </div>
      <div style={{ padding: '8px 14px 12px' }}>
        {grupos.map(([t, ds]) => (
          <div key={t} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0a1628', textTransform: 'uppercase', borderBottom: '2px solid #0a1628', padding: '6px 0 3px', marginBottom: 4 }}>
              {t} ({ds.length})
            </div>
            {ds.map((d, i) => {
              const [rotulo, cor] = statusSimples(d);
              return (
                <div key={d._id} onClick={() => onAbrir(d)} title="Clique pra abrir a demanda"
                  style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0 3px 8px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#9ca3af', fontSize: 11.5, width: 26, textAlign: 'right', flex: '0 0 auto' }}>{i + 1}.</span>
                  <span style={{ fontWeight: 700, color: '#0a1628' }}>{(d.marca || d.cliente_nome || '?').toUpperCase()}</span>
                  <span style={{ color: '#374151' }}>- {d.processo_numero || 's/nº'} -</span>
                  {d.prazo && !semPrazo(d) && (() => {
                    // Data-limite de cumprimento: a 15 dias (ou vencida) grita
                    // em vermelho negrito pra ninguém deixar passar
                    const urgente = d.status === 'pendente' && diasAte(d.prazo) <= 15;
                    return <span title={urgente ? `Faltam ${Math.max(0, diasAte(d.prazo))} dia(s) pro prazo!` : 'Prazo pra cumprir a demanda'}
                      style={{ fontWeight: urgente ? 800 : 400, color: urgente ? '#dc2626' : '#6b7280' }}>{fmtPrazo(d.prazo)} -</span>;
                  })()}
                  <span onClick={(e) => { e.stopPropagation(); alternarStatus(d, rotulo); }}
                    title={rotulo === 'PENDENTE' ? 'Clique pra marcar OK (cumprida)' : rotulo === 'FALTA PAGAR' ? 'Clique pra marcar a taxa como PAGA (vira OK)' : d.taxa_paga ? 'Clique pra desfazer: desmarca a taxa como paga (vira FALTA PAGAR)' : 'Clique pra voltar pra PENDENTE (reabre)'}
                    style={{ fontWeight: 800, color: cor, cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{rotulo}</span>
                  <span onClick={(e) => { e.stopPropagation(); arquivar(d, !d.oculta_simples); }}
                    title={d.oculta_simples ? 'Devolver pra listagem' : 'Arquivar: some desta listagem (o status não muda)'}
                    style={{ marginLeft: 'auto', fontSize: 12, cursor: 'pointer', opacity: 0.55, padding: '0 4px' }}>
                    {d.oculta_simples ? '↩️' : '🗄️'}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Demandas() {
  const navigate = useNavigate();
  const [demandas, setDemandas] = useState(null);
  // Array porque um atalho pode filtrar mais de um tipo (Pagamento cobre
  // Concessão + Taxa Final). O select simples continua mandando um só.
  const [filtroTipo, setFiltroTipo] = useState([]);
  const [modoPainel, setModoPainel] = useState(false);
  const [modoSimples, setModoSimples] = useState(() => localStorage.getItem('demandas_visao_simples') === '1');
  const ligarSimples = (v) => { setModoSimples(v); try { localStorage.setItem('demandas_visao_simples', v ? '1' : ''); } catch (e) { /* segue */ } };
  const [filtroStatus, setFiltroStatus] = useState('pendente');
  const [filtroDe, setFiltroDe] = useState('');
  const [filtroAte, setFiltroAte] = useState('');
  const [busca, setBusca] = useState('');
  const [modalEditar, setModalEditar] = useState(null);
  const [modalDesfecho, setModalDesfecho] = useState(null); // {demanda, acao}
  const [procCopiado, setProcCopiado] = useState(null);
  const [modalPrazos, setModalPrazos] = useState(false);
  const [modalNova, setModalNova] = useState(false);
  const [modalTaxa, setModalTaxa] = useState(null);     // demanda cuja GRU será informada
  const [modalGru, setModalGru] = useState(null);       // demanda pra gerar GRU no portal
  const [modalImportar, setModalImportar] = useState(false);
  const [modalDuplicatas, setModalDuplicatas] = useState(null); // [{importada, automatica}]
  const [filtroTaxa, setFiltroTaxa] = useState('');     // '' | sem | gerada | paga
  const [modalMinuta, setModalMinuta] = useState(null); // demanda de Recurso
  // 🤖 criadas pelo robô da revista (origem 'rpi') × ✍️ lançadas à mão.
  // Abre em Manuais: as automáticas são majoritariamente prazos de anos à
  // frente (prorrogação/provas de uso) e enterravam o que precisa de ação.
  const [abaOrigem, setAbaOrigem] = useState('manual');

  const carregar = useCallback(() => {
    apiJson('/demandas').then(setDemandas).catch(() => setDemandas([]));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const hoje = hojeBrt();
  const todas = demandas || [];

  // Cards do topo (sempre sobre o conjunto todo, sem filtros)
  const pendentes = todas.filter(d => d.status === 'pendente');
  const cont = {
    vencidas: pendentes.filter(d => d.prazo < hoje).length,
    sete: pendentes.filter(d => d.prazo >= hoje && diasAte(d.prazo) <= 7).length,
    quinze: pendentes.filter(d => diasAte(d.prazo) > 7 && diasAte(d.prazo) <= 15).length,
    pendentes: pendentes.length,
    cumpridas: todas.filter(d => d.status === 'cumprida').length,
    // GRU emitida esperando pagamento — dinheiro parado com prazo correndo
    taxaAPagar: pendentes.filter(d => d.taxa_gerada && !d.taxa_paga).length,
  };

  const ehAutomatica = (d) => d.origem === 'rpi';
  const contAuto = todas.filter(d => ehAutomatica(d) && d.status === 'pendente').length;
  const contManual = todas.filter(d => !ehAutomatica(d) && d.status === 'pendente').length;

  // Um único crivo pra lista e pros atalhos. Com ignorarTipo, serve pra contar
  // quantas cada atalho traria — antes eles somavam as duas abas e o número
  // não batia com a lista (chip dizia 5, apareciam 4).
  function passaFiltros(d, ignorarTipo = false, respeitarAba = false, ignorarStatus = false) {
    // Na LISTA, com um tipo escolhido a aba deixa de restringir: a ideia é ver
    // todos daquele tipo, da revista e lançados à mão.
    // Nos PAINÉIS é o contrário — a aba manda (respeitarAba), senão os quadros
    // misturariam 🤖 e ✍️ e a separação Manuais/Automáticas perderia sentido.
    const abaRestringe = respeitarAba || (!filtroTipo.length && !ignorarTipo);
    if (abaRestringe && abaOrigem === 'auto' && !ehAutomatica(d)) return false;
    if (abaRestringe && abaOrigem === 'manual' && ehAutomatica(d)) return false;
    if (!ignorarTipo && filtroTipo.length && !filtroTipo.includes(d.tipo)) return false;
    if (!ignorarStatus && filtroStatus && d.status !== filtroStatus) return false;
    if (filtroTaxa && estadoTaxa(d).k !== filtroTaxa) return false;
    if (filtroDe && d.prazo < filtroDe) return false;
    if (filtroAte && d.prazo > filtroAte) return false;
    if (busca) {
      const b = busca.toLowerCase();
      const dig = busca.replace(/\D/g, '');
      if (!(d.cliente_nome || '').toLowerCase().includes(b) &&
          !(d.marca || '').toLowerCase().includes(b) &&
          !(dig.length >= 3 && (d.processo_numero || '').includes(dig))) return false;
    }
    return true;
  }

  const filtradas = todas.filter(d => passaFiltros(d)); // servidor já ordena pelo prazo
  // Visão simples: mistura pendentes e cumpridas de propósito — a lista é um
  // RELATÓRIO de conferência (pagou × falta pagar), não uma fila de trabalho
  const baseSimples = todas.filter(d => passaFiltros(d, false, false, true));
  // Nos painéis a aba manda, e os atalhos acompanham pra o número do chip
  // bater com o do quadro na tela.
  const baseAtalhos = todas.filter(d => passaFiltros(d, true, modoPainel));

  async function reabrir(d) {
    try { await apiJson(`/demandas/${d._id}/reabrir`, { method: 'PUT' }); carregar(); } catch (e) { window.alert(e.message); }
  }
  async function excluir(d) {
    if (!window.confirm(`Excluir a demanda "${d.tipo}" de ${d.cliente_nome}? Essa ação não tem volta.`)) return;
    try { await apiJson(`/demandas/${d._id}`, { method: 'DELETE' }); carregar(); } catch (e) { window.alert(e.message); }
  }
  // Traz uma automática da revista pra sua fila de trabalho, sem mexer na original
  async function copiarParaManual(d) {
    if (!window.confirm(`Copiar "${d.tipo}" de ${d.cliente_nome} para a aba Manuais?\n\nA automática original continua onde está.`)) return;
    try {
      await apiJson(`/demandas/${d._id}/copiar-manual`, { method: 'POST' });
      setAbaOrigem('manual');
      carregar();
    } catch (e) { window.alert(e.message); }
  }
  function copiarProcesso(d) {
    copiarTexto(d.processo_numero);
    setProcCopiado(d._id);
    setTimeout(() => setProcCopiado(null), 2000);
  }

  const cardResumo = (valor, rotulo, cor) => (
    <span style={{ color: cor }}>{rotulo}: <strong>{valor}</strong></span>
  );

  return (
    <div style={{ maxWidth: 960 }}>
      {modalDuplicatas && <ModalDuplicatas duplicatas={modalDuplicatas}
        onFechar={() => { setModalDuplicatas(null); carregar(); }} />}
      {modalImportar && <ModalImportarPepi onFechar={() => setModalImportar(false)}
        onSalvo={(dups) => {
          setModalImportar(false); setAbaOrigem('manual'); carregar();
          if (dups && dups.length) setModalDuplicatas(dups);
        }} />}
      {modalTaxa && <ModalTaxa demanda={modalTaxa} onFechar={() => setModalTaxa(null)}
        onSalvo={() => { setModalTaxa(null); carregar(); }} />}
      {modalGru && <ModalGerarGru demanda={modalGru} onFechar={() => setModalGru(null)}
        onIrParaTaxa={() => { const d = modalGru; setModalGru(null); setModalTaxa(d); }} />}
      {modalNova && <ModalNovaDemanda onFechar={() => setModalNova(false)}
        onSalvo={() => { setModalNova(false); setAbaOrigem('manual'); carregar(); }} />}
      {modalEditar && <ModalDemanda demanda={modalEditar} onFechar={() => setModalEditar(null)} onSalvo={() => { setModalEditar(null); carregar(); }} />}
      {modalDesfecho && <ModalDesfechoDemanda demanda={modalDesfecho.demanda} acao={modalDesfecho.acao}
        onFechar={() => setModalDesfecho(null)} onSalvo={() => { setModalDesfecho(null); carregar(); }} />}

      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 700, color: '#0a1628', margin: 0 }}>⚖️ Demandas Processuais</h1>
          <p style={{ color: '#6b7280', fontSize: 12, margin: '1px 0 0' }}>Prazos do INPI lançados nos processos dos clientes — o mais urgente primeiro.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setModalNova(true)}
            style={{ background: '#0a1628', border: '1px solid #0a1628', borderRadius: 4, padding: '5px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
            ＋ Nova demanda
          </button>
          <button onClick={() => setModalImportar(true)} title="Subir o PDF da página Meus Pedidos do pePI e lançar as demandas da semana de uma vez"
            style={{ background: '#fff', border: '1px solid #0a1628', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: '#0a1628', cursor: 'pointer' }}>
            📄 Importar movimentações
          </button>
          <label title="Visão simples: listagem enxuta por tipo — marca, processo e pagou/falta pagar"
            onClick={() => ligarSimples(!modoSimples)}
            style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none', border: '1px solid ' + (modoSimples ? '#15803d' : '#c8d0da'), borderRadius: 20, padding: '4px 12px', background: modoSimples ? '#f0fdf4' : '#fff' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: modoSimples ? '#15803d' : '#374151' }}>📋 Visão simples</span>
            <span style={{ width: 34, height: 18, borderRadius: 10, background: modoSimples ? '#15803d' : '#cbd5e1', position: 'relative', transition: 'background .15s', flex: '0 0 auto' }}>
              <span style={{ position: 'absolute', top: 2, left: modoSimples ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.3)' }} />
            </span>
          </label>
          <button onClick={() => setModoPainel(v => !v)}
            title={modoPainel ? 'Voltar pra lista' : 'Ver em quadros por tipo de demanda'}
            style={{ background: modoPainel ? '#0a1628' : '#fff', border: '1px solid #0a1628', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: modoPainel ? '#fff' : '#0a1628', cursor: 'pointer' }}>
            {modoPainel ? '☰ Lista' : '▦ Painéis'}
          </button>
          <button onClick={() => setModalPrazos(true)}
            style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#0a1628', cursor: 'pointer' }}>
            📖 Prazos e códigos do INPI
          </button>
        </div>
      </div>

      {modalPrazos && (
        <div onClick={() => setModalPrazos(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 6, maxWidth: 700, width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: 0 }}>📖 Prazos e códigos do INPI (Manual de Marcas)</h2>
              <button onClick={() => setModalPrazos(false)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                <thead><tr>{['Evento', 'Prazo', 'Conta de', 'Se perder', 'Cód.'].map(h => <th key={h} style={{ background: '#0a1628', color: '#fff', textAlign: 'left', padding: '4px 8px' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {[
                    ['Exigência FORMAL', '5 dias corridos ⚠️', '1º dia útil pós-publicação', 'Pedido INEXISTENTE', '338'],
                    ['Oposição de terceiro', '60 dias', 'Publicação do pedido', 'Não conhecida', '332 / 3022*'],
                    ['Manifestação à oposição', '60 dias', 'Notificação na RPI', 'Não conhecida (opcional)', '339'],
                    ['Exigência de mérito', '60 dias', 'Publicação', 'Arquivamento definitivo', '340'],
                    ['Pagar taxa de concessão', '60 dias (+30 c/ taxa extra)', 'Publicação do deferimento', 'Arquivamento', 'GRU'],
                    ['Recurso contra indeferimento', '60 dias', 'Publicação do indeferimento', 'Decisão definitiva', '3000'],
                    ['Contrarrazões a recurso', '60 dias', 'Notificação', '—', '3015'],
                    ['Nulidade administrativa (PAN)', '180 dias', 'Publicação da concessão', 'Só via judicial (5 anos)', '336'],
                    ['Defesa do titular no PAN', '60 dias', 'Intimação', 'Decidido sem defesa', '3015'],
                    ['Procuração (se não anexada)', '60 dias', 'Depósito', 'Arquivamento DEFINITIVO', '381'],
                    ['Comprovante de prioridade', '4 meses', 'Depósito', 'Perde a prioridade', '381'],
                    ['Vigência do registro', '10 anos', 'Publicação da concessão', '—', '—'],
                    ['Prorrogação ordinária', 'Último ano de vigência', '9 anos da concessão', 'Cai na extraordinária', '374**'],
                    ['Prorrogação extraordinária', '6 meses (taxa extra)', 'Dia seguinte ao fim da vigência', 'EXTINÇÃO do registro', '375**'],
                    ['Caducidade (dever de uso)', 'A partir do 5º ano', 'Concessão', 'Registro atacável', '337'],
                    ['Defesa em caducidade', '60 dias', 'Notificação', 'Registro caduca', '—'],
                  ].map((l, i) => (
                    <tr key={i} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
                      {l.map((c, j) => <td key={j} style={{ border: '1px solid #e2e8f0', padding: '3px 8px', fontWeight: j === 1 ? 700 : 400, color: j === 1 && c.includes('5 dias') ? '#dc2626' : '#111827' }}>{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 10.5, color: '#6b7280', marginTop: 8 }}>
              * 3022 = oposição restrita ao art. 124 XIX, mais barata — ATENÇÃO: anexos não são lidos, argumente no corpo da petição. ** 374/375 não têm formulário: a data do PAGAMENTO da GRU é o protocolo.
              Fonte: Manual de Marcas do INPI (cópia de estudo em docs/manual-inpi/ · 22/07/2026). A revista sai toda terça — quase todos os prazos contam da publicação nela.
            </p>
          </div>
        </div>
      )}

      {/* Abas: manuais × automáticas (robô da revista) × as duas juntas */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {[
          { k: 'manual', label: `✍️ Manuais (${contManual})`, title: 'Lançadas por você na ficha do cliente ou nos Marcos da RPI' },
          { k: 'auto', label: `🤖 Automáticas da revista (${contAuto})`, title: 'Criadas sozinhas pelo sistema quando a RPI é importada' },
          { k: 'todas', label: `📚 Todas (${contManual + contAuto})`, title: 'As duas origens juntas — visão completa, também nos painéis' },
        ].map(t => (
          <button key={t.k} onClick={() => setAbaOrigem(t.k)} title={t.title} style={{
            padding: '6px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
            border: '1px solid ' + (abaOrigem === t.k ? '#0a1628' : '#d1d5db'),
            background: abaOrigem === t.k ? '#0a1628' : '#fff', color: abaOrigem === t.k ? '#fff' : '#374151',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Resumo compacto */}
      <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, padding: '6px 14px', marginBottom: 8, display: 'flex', gap: 20, fontSize: 13, flexWrap: 'wrap' }}>
        {cardResumo(cont.vencidas, '⚫ Vencidas', '#111827')}
        {cardResumo(cont.sete, '🔴 Até 7 dias', '#dc2626')}
        {cardResumo(cont.quinze, '🟠 8–15 dias', '#b45309')}
        {cardResumo(cont.pendentes, 'Pendentes', '#374151')}
        {cardResumo(cont.cumpridas, '✓ Cumpridas', '#16a34a')}
        {cont.taxaAPagar > 0 && cardResumo(cont.taxaAPagar, '💰 GRU a pagar', '#b45309')}
      </div>

      {/* Filtros */}
      <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, marginBottom: 8 }}>
        <div style={{ background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '3px 10px', fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Filtros
        </div>
        {/* Uma linha só. O index.css tem 'input, select { width: 100% }' global,
            que empilhava tudo — largura explícita no inline vence essa regra.
            A classe filtros-row mantém o empilhamento no celular (media query). */}
        <div className="filtros-row" style={{ padding: '6px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filtroTipo.length === 1 ? filtroTipo[0] : ''}
            onChange={e => setFiltroTipo(e.target.value ? [e.target.value] : [])}
            style={{ ...inp, width: 178, flex: '0 0 auto' }}>
            <option value="">Todas as demandas</option>
            {TIPOS_DEMANDA.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ ...inp, width: 128, flex: '0 0 auto' }}>
            <option value="pendente">Pendentes</option>
            <option value="cumprida">Cumpridas</option>
            <option value="nao_cumprida">Não cumpridas</option>
            <option value="">Todas</option>
          </select>
          <select value={filtroTaxa} onChange={e => setFiltroTaxa(e.target.value)} style={{ ...inp, width: 136, flex: '0 0 auto' }}>
            <option value="">Taxa: todas</option>
            <option value="sem">💰 Sem GRU</option>
            <option value="gerada">💰 GRU a pagar</option>
            <option value="paga">💰 Taxa paga</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flex: '0 0 auto' }}>
            <span style={{ fontSize: 11.5, color: '#6b7280', whiteSpace: 'nowrap' }}>Prazo</span>
            <input type="date" value={filtroDe} onChange={e => setFiltroDe(e.target.value)} style={{ ...inp, width: 128 }} />
            <span style={{ fontSize: 11.5, color: '#6b7280' }}>a</span>
            <input type="date" value={filtroAte} onChange={e => setFiltroAte(e.target.value)} style={{ ...inp, width: 128 }} />
          </div>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Cliente, marca ou processo…" style={{ ...inp, flex: '1 1 130px', minWidth: 110 }} />
          {(filtroTipo.length || filtroStatus !== 'pendente' || filtroTaxa || filtroDe || filtroAte || busca) && (
            <button onClick={() => { setFiltroTipo([]); setFiltroStatus('pendente'); setFiltroTaxa(''); setFiltroDe(''); setFiltroAte(''); setBusca(''); }}
              style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>× Limpar</button>
          )}
        </div>

        {/* Atalhos por grupo — o de pagamento cobre Concessão E Taxa Final */}
        <div style={{ padding: '0 10px 7px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {GRUPOS_TIPO.map(gr => {
            const ativo = gr.tipos.length === filtroTipo.length && gr.tipos.every(t => filtroTipo.includes(t));
            const n = baseAtalhos.filter(d => gr.tipos.includes(d.tipo)).length;
            return (
              <button key={gr.key} onClick={() => setFiltroTipo(ativo ? [] : gr.tipos)}
                title={ativo ? 'Clique pra tirar o filtro' : `Filtrar por ${gr.label}`}
                style={{
                  padding: '3px 11px', fontSize: 11.5, fontWeight: 700, borderRadius: 20, cursor: 'pointer',
                  border: '1px solid ' + (ativo ? gr.cor : '#c8d0da'),
                  background: ativo ? gr.cor : '#fff',
                  color: ativo ? '#fff' : '#0a1628', whiteSpace: 'nowrap',
                }}>
                {gr.icone} {gr.curto || gr.label}{n > 0 ? ` (${n})` : ''}
              </button>
            );
          })}
          {filtroTipo.length > 0 && (
            <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginLeft: 4 }}>
              mostrando <strong>{filtroTipo.join(' + ')}</strong> das duas abas (🤖 revista + ✍️ manuais)
            </span>
          )}
        </div>
      </div>

      {/* Visão SIMPLES: listagem de conferência por tipo (pagou × falta pagar) */}
      {modoSimples && demandas ? (
        <VisaoSimples demandas={baseSimples} onAbrir={setModalEditar} onRecarregar={carregar} onTaxa={setModalTaxa} onNova={() => setModalNova(true)} />
      ) : null}

      {/* Painéis por tipo (estilo subagendas da Agenda) — usa a base que
          ignora o filtro de tipo, senão os outros quadros ficariam vazios */}
      {!modoSimples && modoPainel && demandas ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
          {[...GRUPOS_TIPO, { key: 'outras', label: 'Outras', icone: '📦', cor: '#6b7280', tipos: null }].map(gr => {
            const ds = gr.tipos
              ? baseAtalhos.filter(d => gr.tipos.includes(d.tipo))
              : baseAtalhos.filter(d => !GRUPOS_TIPO.some(g => g.tipos.includes(d.tipo)));
            const semGru = ds.filter(d => !d.taxa_paga).length;
            return (
              <div key={gr.key} style={{ background: '#fff', border: '1px solid #c8d0da', borderTop: `4px solid ${gr.cor}`, borderRadius: 6, display: 'flex', flexDirection: 'column', minHeight: 150, maxHeight: 430 }}>
                <div style={{ padding: '6px 10px', borderBottom: '1px solid #e8eaed', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: '#0a1628', lineHeight: 1.25 }}>{gr.icone} {(gr.curto || gr.label).toUpperCase()}</span>
                  <span style={{ background: gr.cor, color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 800 }}>{ds.length}</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {ds.map((d, i) => {
                    const u = urgencia(d);
                    const t = estadoTaxa(d);
                    return (
                      <div key={d._id} onClick={() => setModalEditar(d)}
                        title={`${d.tipo} · ${d.cliente_nome}${d.processo_numero ? ' · proc. ' + d.processo_numero : ''}`}
                        onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(0.97)'; }}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
                        style={{ padding: '5px 9px', borderBottom: '1px solid #f3f4f6', background: i % 2 ? '#fafafa' : '#fff', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                          <span style={{ fontSize: 11.5, fontWeight: 800, color: '#0a1628', whiteSpace: 'nowrap' }}>{fmtBR(d.prazo)}</span>
                          <span style={{ background: u.bg, color: u.cor, padding: '0 5px', borderRadius: 3, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{u.rotulo}</span>
                          <span style={{ marginLeft: 'auto', fontSize: 10 }}>{ehAutomatica(d) ? '🤖' : '✍️'}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: '#111827', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.cliente_nome}</div>
                        <div style={{ fontSize: 10.5, color: '#6b7280', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                          {d.processo_numero && <span>proc. {d.processo_numero}</span>}
                          <span style={{ color: t.cor, fontWeight: 700 }}>{t.rotulo}</span>
                        </div>
                      </div>
                    );
                  })}
                  {ds.length === 0 && <div style={{ padding: 12, fontSize: 11.5, color: '#c3cad3', textAlign: 'center' }}>Nenhuma demanda</div>}
                </div>
                <div style={{ borderTop: '1px solid #e8eaed', padding: '4px 10px', display: 'flex', gap: 12, fontSize: 10.5, color: '#374151', background: '#f9fafb', borderRadius: '0 0 6px 6px' }}>
                  <span>Total: <b>{ds.length}</b></span>
                  {semGru > 0 && <span style={{ color: '#b45309' }}>Taxa em aberto: <b>{semGru}</b></span>}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Lista */}
      {modoSimples || modoPainel ? null : !demandas ? (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Carregando…</div>
      ) : filtradas.length === 0 ? (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          {todas.length === 0
            ? <>Nenhuma demanda lançada ainda. Elas nascem no cadastro do cliente → aba Processos → <strong>⚖️ + Demanda</strong>.</>
            : 'Nenhuma demanda neste filtro.'}
        </div>
      ) : (
        <div style={{ border: '1px solid #c8d0da', borderRadius: 4, overflowX: 'auto' }}>
          <div style={{ background: '#0a1628', display: 'grid', gridTemplateColumns: '150px 130px 1fr 170px', gap: 0, padding: '5px 10px', minWidth: 680 }}>
            {['Prazo', 'Demanda', 'Cliente / Processo', 'Ações'].map(h => (
              <span key={h} style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</span>
            ))}
          </div>
          {filtradas.map((d, i) => {
            const u = urgencia(d);
            const t = estadoTaxa(d);
            return (
              <div key={d._id} style={{
                display: 'grid', gridTemplateColumns: '150px 130px 1fr 170px',
                alignItems: 'center', gap: 0, padding: '6px 10px', minWidth: 680, boxSizing: 'border-box',
                borderBottom: '1px solid #e8eaed', background: i % 2 === 0 ? '#fafafa' : '#fff',
              }}>
                {/* Prazo + urgência */}
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0a1628' }}>{fmtBR(d.prazo)}</div>
                  <span style={{ background: u.bg, color: u.cor, padding: '1px 7px', borderRadius: 3, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{u.rotulo}</span>
                </div>

                {/* Tipo + de onde veio (importa quando o filtro por tipo junta as duas abas) */}
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0a1628', paddingRight: 6 }}>
                  {d.tipo}
                  {(filtroTipo.length > 0 || abaOrigem === 'todas') && (
                    <span title={ehAutomatica(d) ? 'Criada pelo robô da revista' : 'Lançada à mão'}
                      style={{ marginLeft: 5, fontSize: 11, fontWeight: 400 }}>
                      {ehAutomatica(d) ? '🤖' : '✍️'}
                    </span>
                  )}
                </div>

                {/* Cliente / processo / obs */}
                <div style={{ fontSize: 12, paddingRight: 8, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.cliente_nome}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    {d.marca && <span>™ {d.marca}</span>}
                    {d.processo_numero && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        proc. <strong>{d.processo_numero}</strong>
                        <button onClick={() => copiarProcesso(d)} title="Copiar número do processo"
                          style={{ background: procCopiado === d._id ? '#dcfce7' : '#f3f4f6', color: procCopiado === d._id ? '#15803d' : '#374151', border: '1px solid #d1d5db', borderRadius: 3, padding: '0 4px', fontSize: 10, cursor: 'pointer', lineHeight: '14px' }}>
                          {procCopiado === d._id ? '✓' : '📋'}
                        </button>
                      </span>
                    )}
                    <button onClick={() => setModalTaxa(d)} title="Informar se a GRU foi gerada e se a taxa foi paga"
                      style={{ background: t.bg, color: t.cor, border: '1px solid ' + t.borda, borderRadius: 3, padding: '0 6px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', lineHeight: '16px', whiteSpace: 'nowrap' }}>
                      {t.rotulo}
                      {Number.isFinite(Number(d.taxa_valor)) && d.taxa_valor !== null && d.taxa_valor !== ''
                        ? ` · R$ ${Number(d.taxa_valor).toFixed(2).replace('.', ',')}` : ''}
                    </button>
                  </div>
                  {d.observacao && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{d.observacao}</div>}
                  {d.resultado && <div style={{ fontSize: 11, color: d.status === 'cumprida' ? '#15803d' : '#b91c1c', marginTop: 1 }}>{d.status === 'cumprida' ? '✓' : '✗'} {d.resultado} <span style={{ color: '#9ca3af' }}>({d.cumprido_por})</span></div>}
                </div>

                {/* Ações */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {d.status === 'pendente' ? (
                    <>
                      <button onClick={() => setModalDesfecho({ demanda: d, acao: 'cumprir' })}
                        style={{ background: '#16a34a', color: '#fff', padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>✓ Cumprir</button>
                      <button onClick={() => setModalDesfecho({ demanda: d, acao: 'nao' })} title="Não cumprida (com motivo)"
                        style={{ background: '#fff', color: '#dc2626', padding: '2px 7px', borderRadius: 3, fontSize: 11, fontWeight: 700, border: '1px solid #fecaca', cursor: 'pointer' }}>✗</button>
                      <button onClick={() => setModalEditar(d)} title="Editar tipo/prazo/observação"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '0 2px' }}>✏️</button>
                    </>
                  ) : (
                    <button onClick={() => reabrir(d)} title="Reabrir"
                      style={{ background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 3, fontSize: 11, border: '1px solid #d1d5db', cursor: 'pointer' }}>↩</button>
                  )}
                  {/* ⚖️ Minuta DESATIVADA a pedido do Vagner (12/08/2026, economia de
                      crédito de IA) — pra reativar, restaure este bloco e o mount
                      do minutaRecursoRouter no backend/src/index.js:
                  {ehVagner() && ['Recurso', 'Exigência de Mérito', 'Manifestação', 'Oposição'].includes(d.tipo) && d.processo_numero && (
                    <button onClick={() => setModalMinuta(d)} title={`Gerar minuta de ${d.tipo.toLowerCase()} com o modelo do escritório`}
                      style={{ background: '#4c1d95', color: '#fff', padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>⚖️ Minuta</button>
                  )} */}
                  {ehAutomatica(d) && d.status === 'pendente' && (
                    <button onClick={() => copiarParaManual(d)} title="Copiar para a aba Manuais (a automática original continua onde está)"
                      style={{ background: '#fff', color: '#0a1628', padding: '2px 7px', borderRadius: 3, fontSize: 11, fontWeight: 600, border: '1px solid #c8d0da', cursor: 'pointer', whiteSpace: 'nowrap' }}>✍️ Copiar</button>
                  )}
                  {d.status === 'pendente' && d.processo_numero && !d.taxa_paga && (
                    <button onClick={() => setModalGru(d)} title="Gerar a GRU no portal do INPI (dados prontos pra copiar)"
                      style={{ background: '#166534', color: '#fff', padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>🧾 Gerar GRU</button>
                  )}
                  <button onClick={() => navigate(`/clientes/${d.cliente_id}/editar`)}
                    style={{ background: '#e8edf2', color: '#0a1628', padding: '2px 8px', borderRadius: 3, fontSize: 11, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>Cadastro</button>
                  {ehVagner() && (
                    <button onClick={() => excluir(d)} title="Excluir (só administrador)"
                      style={{ background: 'none', color: '#dc2626', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '0 2px', lineHeight: 1 }}>✕</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalMinuta && <ModalMinutaRecurso demanda={modalMinuta} onFechar={() => setModalMinuta(null)} />}

      <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6 }}>
        ⚖️ Os prazos aparecem no calendário 📅 e no calendário do celular · aviso no WhatsApp do Vagner 7 dias antes, 1 dia antes e no dia.
      </div>
    </div>
  );
}
